import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Interface representing a reauth token with metadata
 */
export interface ReauthToken {
  token: string;
  receivedAt: Date;
}

/**
 * Service for centralized reauth token management.
 *
 * This service manages the short-lived re-authentication token used for
 * sensitive operations. The token is valid for 5 minutes and is automatically
 * refreshed by the backend on each API call.
 *
 * @example
 * ```typescript
 * // Set a new token after successful re-authentication
 * this.reauthTokenService.setReauthToken('token-string');
 *
 * // Subscribe to token changes
 * this.reauthTokenService.reauthToken$.subscribe(token => {
 *   console.log('Token changed:', token);
 * });
 *
 * // Get current token synchronously
 * const token = this.reauthTokenService.getCurrentToken();
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ReauthTokenService {
  private reauthTokenSubject = new BehaviorSubject<ReauthToken | null>(null);

  /**
   * Observable that emits whenever the reauth token changes.
   * Components/services can subscribe to this to react to token updates.
   * Emits null when no token is active.
   */
  public readonly reauthToken$: Observable<ReauthToken | null> =
    this.reauthTokenSubject.asObservable();

  /**
   * Get the current reauth token (synchronous access)
   * @returns The current reauth token or null if no token is set
   */
  getCurrentToken(): ReauthToken | null {
    return this.reauthTokenSubject.value;
  }

  /**
   * Get just the token string (convenience method)
   * @returns The token string or null if no token is set
   */
  getTokenString(): string | null {
    return this.reauthTokenSubject.value?.token || null;
  }

  /**
   * Store a new reauth token (e.g., after successful re-authentication
   * or when received in an API response)
   *
   * @param token The reauth token string
   */
  setReauthToken(token: string): void {
    if (!token) {
      console.warn('Attempted to set empty reauth token');
      return;
    }

    this.reauthTokenSubject.next({
      token,
      receivedAt: new Date()
    });
  }

  /**
   * Clear the reauth token (e.g., on logout, explicit invalidation, or timer expiry)
   */
  clearReauthToken(): void {
    this.reauthTokenSubject.next(null);
  }

  /**
   * Check if a valid reauth token exists
   * @returns true if a token is currently set
   */
  hasReauthToken(): boolean {
    return this.reauthTokenSubject.value !== null;
  }
}
